import React, { useState, useCallback } from 'react';
import { PlusCircle, MinusCircle, Star, ExternalLink } from 'lucide-react';
import {
  getHeatmapCellColor,
  formatMultiplier,
  formatPremiumGain,
  formatScenarioLabel,
} from '../../utils/elasticityCalculations';
import { getSimpleStrikePositionLabel } from '../../utils/presetMappings';

const formatStrike = (s) => {
  const num = parseFloat(s);
  if (isNaN(num)) return s;
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
};

const fmtPrice = (v) => (v != null && !isNaN(v) ? `$${Number(v).toFixed(2)}` : '--');

function ContractHeatmapCard({
  row,
  rowIndex,
  scenarioArray,
  displayMode,
  registerStripRef,
  onStripScroll,
  isFavorited,
  isSaving,
  onToggleFavorite,
  onSaveToWatchlist,
  onRemoveRow,
  onTickerClick,
}) {
  const [expandedScenario, setExpandedScenario] = useState(null);

  const strike = formatStrike(row['Strike']);
  const expiration = row['Expiration'];
  const positionType = row['Position Type'] || 'Buy';
  const optionType = row['Option Type'] || 'call';
  const ticker = row['Ticker'];
  const currentPremium = parseFloat(row['Current Premium']);
  const currentUnderlying = parseFloat(row['Underlying Price']);
  const strikePosLabel = row['Strike Position'] !== undefined && row['Strike Position'] !== null
    ? getSimpleStrikePositionLabel(row['Strike Position'], optionType)
    : null;

  const setStripRef = useCallback((el) => {
    registerStripRef(rowIndex, el);
  }, [registerStripRef, rowIndex]);

  const handleScroll = useCallback((e) => {
    onStripScroll(rowIndex, e.currentTarget.scrollLeft);
  }, [onStripScroll, rowIndex]);

  const handleChipTap = (pct) => {
    setExpandedScenario((prev) => (prev === pct ? null : pct));
  };

  const expandedCell = expandedScenario != null ? row._heatmap?.[expandedScenario] : null;
  const expandedSimUnd = expandedCell?.simulatedStock;
  const expandedSimPrem = expandedCell?.projectedPremium;
  const breakeven = row._breakeven;

  return (
    <div className="mhm-card">
      <div className="mhm-card-header">
        <button
          type="button"
          className="mhm-card-identity"
          onClick={() => onTickerClick(row)}
          title="View Premium & Underlying Charts"
        >
          <div className="mhm-card-identity-line1">
            <span className="mhm-card-ticker">{ticker}</span>
            <span className="mhm-card-position">{positionType}</span>
            <span className="mhm-card-option-type">{optionType}</span>
            <ExternalLink className="mhm-card-ext-icon" />
          </div>
          <div className="mhm-card-identity-line2">
            ${strike}{strikePosLabel ? ` · ${strikePosLabel}` : ''}
            {currentPremium ? ` · Prem ${fmtPrice(currentPremium)}` : ''}
          </div>
        </button>
        <div className="mhm-card-expiration">{expiration}</div>
      </div>

      <div className="mhm-card-actions">
        <button
          type="button"
          onClick={() => onToggleFavorite(row)}
          className={`mhm-action-btn ${isFavorited ? 'mhm-action-btn-favorited' : ''}`}
          title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Star className="mhm-action-icon" fill={isFavorited ? 'currentColor' : 'none'} />
        </button>
        <button
          type="button"
          onClick={() => onSaveToWatchlist(row)}
          disabled={isSaving}
          className="mhm-action-btn"
          title="Save to Watchlist"
        >
          {isSaving ? (
            <span className="mhm-action-spinner" />
          ) : (
            <PlusCircle className="mhm-action-icon" />
          )}
        </button>
        <button
          type="button"
          onClick={() => onRemoveRow(row)}
          className="mhm-action-btn mhm-action-btn-remove"
          title="Remove from results"
        >
          <MinusCircle className="mhm-action-icon" />
        </button>
      </div>

      <div
        ref={setStripRef}
        onScroll={handleScroll}
        className="mhm-chip-strip"
      >
        {scenarioArray.map((pct) => {
          const cell = row._heatmap?.[pct];
          const raw = displayMode === 'multiplier' ? cell?.multiplier : cell?.premiumGain;
          const formatted = displayMode === 'multiplier'
            ? formatMultiplier(raw)
            : formatPremiumGain(raw);
          const style = getHeatmapCellColor(raw, displayMode);
          const isActive = expandedScenario === pct;

          return (
            <button
              key={pct}
              type="button"
              onClick={() => handleChipTap(pct)}
              className={`mhm-chip ${isActive ? 'mhm-chip-active' : ''}`}
              style={style}
            >
              <span className="mhm-chip-scenario">{formatScenarioLabel(pct)}</span>
              <span className="mhm-chip-value">{formatted}</span>
            </button>
          );
        })}
      </div>

      {expandedScenario != null && (
        <div className="mhm-chip-detail">
          <div className="mhm-chip-detail-row">
            <span className="mhm-chip-detail-label">@ {formatScenarioLabel(expandedScenario)} underlying</span>
          </div>
          <div className="mhm-chip-detail-row">
            <span className="mhm-chip-detail-label">Premium:</span>
            <span>{fmtPrice(currentPremium)} → {fmtPrice(expandedSimPrem)}</span>
          </div>
          <div className="mhm-chip-detail-row">
            <span className="mhm-chip-detail-label">Underlying:</span>
            <span>{fmtPrice(currentUnderlying)} → {fmtPrice(expandedSimUnd)}</span>
          </div>
          {breakeven != null && (
            <div className="mhm-chip-detail-row mhm-chip-detail-breakeven">
              <span className="mhm-chip-detail-label">Breakeven:</span>
              <span>{fmtPrice(breakeven)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContractHeatmapCard;
